import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { FEEDBACK_RATINGS, MAX_FEEDBACK_COMMENT_LENGTH } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { StateView } from '@/components/common/StateView';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { submitFeedback } from '@/services/firebase/callables';
import { cn } from '@/lib/utils';
import { toUserMessage } from '@/utils/errors';
import { validateFeedback } from './validation';
import { CheckCircle2 } from 'lucide-react';

const RATING_LABEL: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very good',
  5: 'Excellent',
};

/** Ratings at or below this keep the comment box; above it, the guest is sent to Google. */
const REVIEW_PROMPT_MIN_RATING = 4;

/**
 * A star rating and an optional comment. Always available; no service switch
 * governs it.
 *
 * When the hotel has a `googleReviewUrl` on its profile the comment box is
 * hidden until the guest rates three or fewer stars — an unhappy stay is told
 * to the hotel privately, while four or five stars are recorded and then
 * pointed at the public review. With no review link on the profile nothing
 * changes: the comment box is always there, as before.
 */
export function FeedbackPage() {
  const { location, session, services } = useReadyGuest();
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reviewPrompt, setReviewPrompt] = useState(false);
  const inFlight = useRef(false);

  const reviewUrl = services?.googleReviewUrl ?? null;
  // Keyed on the committed rating, not `hover`, so the card does not flicker
  // in and out while the guest slides across the stars.
  const showComment = !reviewUrl || (rating !== null && rating < REVIEW_PROMPT_MIN_RATING);

  const handleSubmit = async () => {
    // A comment typed at three stars and then raised to five is not sent: the
    // guest can no longer see it, so it is no longer theirs to submit.
    const validation = validateFeedback({ rating, comment: showComment ? comment : '' });
    if (!validation.ok) {
      toast.warning(validation.error);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      await submitFeedback({
        hotelId: location.hotelId,
        roomId: location.roomId,
        rating: validation.rating,
        ...(validation.comment ? { comment: validation.comment } : {}),
      });
      setDone(true);
      if (reviewUrl && validation.rating >= REVIEW_PROMPT_MIN_RATING) {
        setReviewPrompt(true);
      }
    } catch (error) {
      toast.error(toUserMessage(error));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const shown = hover ?? rating;

  return (
    <AppShell>
      <PageHeader title="Feedback" subtitle={session.hotelName} />
      {done ? (
        <StateView
          icon={CheckCircle2}
          tone="success"
          title="Thank you!"
          message="Your feedback helps us make every stay better."
        >
          <Button asChild>
            <Link to="/">Back to home</Link>
          </Button>
        </StateView>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-2">
              <p className="text-base font-semibold">How was your stay?</p>
              <div
                className="flex gap-1"
                role="radiogroup"
                aria-label="Star rating"
                onMouseLeave={() => setHover(null)}
              >
                {FEEDBACK_RATINGS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={rating === value}
                    aria-label={`${value} star${value > 1 ? 's' : ''}`}
                    className="rounded-full p-1 transition-transform active:scale-90 focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none"
                    onClick={() => setRating(value)}
                    onMouseEnter={() => setHover(value)}
                    disabled={submitting}
                  >
                    <Star
                      className={cn(
                        'size-9 transition-colors',
                        shown !== null && value <= shown
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground/40',
                      )}
                    />
                  </button>
                ))}
              </div>
              <p className="text-muted-foreground h-5 text-sm" aria-live="polite">
                {shown ? RATING_LABEL[shown] : 'Tap a star to rate'}
              </p>
            </CardContent>
          </Card>

          {showComment ? (
            <Card>
              <CardContent className="flex flex-col gap-2">
                <Label htmlFor="fb-comment">Tell us about your experience</Label>
                <Textarea
                  id="fb-comment"
                  placeholder="What did you enjoy? What could we do better?"
                  value={comment}
                  maxLength={MAX_FEEDBACK_COMMENT_LENGTH}
                  onChange={(event) => setComment(event.target.value)}
                  disabled={submitting}
                  className="min-h-32"
                />
                <p className="text-muted-foreground text-right text-xs tabular-nums">
                  {comment.length}/{MAX_FEEDBACK_COMMENT_LENGTH}
                </p>
              </CardContent>
            </Card>
          ) : null}

          <Button size="lg" onClick={handleSubmit} disabled={submitting || rating === null} aria-busy={submitting}>
            {submitting ? 'Sending…' : 'Submit Feedback'}
          </Button>
        </div>
      )}

      {reviewUrl ? (
        <Dialog open={reviewPrompt} onOpenChange={setReviewPrompt}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Thank you for rating us!</DialogTitle>
              <DialogDescription>
                Kindly provide your review in Google review.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button asChild size="lg" onClick={() => setReviewPrompt(false)}>
                <a href={reviewUrl} target="_blank" rel="noopener noreferrer">
                  Google review
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </AppShell>
  );
}
